import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Save } from "lucide-react";
import { api, formatErr } from "@/lib/api";

const EMPTY = { question: "", answer: "", page_type: "global", page_id: "", active: true, order: 0 };

export default function AdminFaqs() {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/faqs").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.question.trim() || !draft.answer.trim()) return toast.error("Question et réponse requises");
    try { await api.post("/admin/faqs", { ...draft, order: Number(draft.order) || 0 }); setDraft(EMPTY); load(); toast.success("FAQ ajoutée"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const save = async (f) => {
    try { await api.patch(`/admin/faqs/${f.id}`, { ...f, order: Number(f.order) || 0 }); setEditing(null); load(); toast.success("FAQ mise à jour"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette FAQ ?")) return;
    try { await api.delete(`/admin/faqs/${id}`); load(); toast.success("Supprimé"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div>
      <div className="font-heading text-3xl font-black uppercase tracking-tight">FAQ</div>
      <div className="h-1 w-16 bg-brand mt-2 mb-6" />

      <div className="bg-ink-900 border border-ink-700 p-4 mb-6">
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Nouvelle question</div>
        <input className="input-admin" placeholder="Question" value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })} />
        <textarea className="input-admin mt-2 h-24 py-2" placeholder="Réponse" value={draft.answer} onChange={(e) => setDraft({ ...draft, answer: e.target.value })} />
        <div className="grid sm:grid-cols-4 gap-2 mt-2">
          <input className="input-admin" placeholder="page_type (global, service, ville…)" value={draft.page_type} onChange={(e) => setDraft({ ...draft, page_type: e.target.value })} />
          <input className="input-admin" placeholder="page_id (optionnel)" value={draft.page_id || ""} onChange={(e) => setDraft({ ...draft, page_id: e.target.value })} />
          <input className="input-admin" type="number" placeholder="Ordre" value={draft.order} onChange={(e) => setDraft({ ...draft, order: e.target.value })} />
          <button onClick={create} className="bg-brand hover:bg-brand-600 text-white px-4 py-2.5 sm:py-0 inline-flex items-center justify-center gap-2 font-bold uppercase tracking-wider text-sm transition-colors">
            <Plus size={14} /> Ajouter
          </button>
        </div>
      </div>

      <div className="grid gap-3">
        {rows.map((f) => (
          <div key={f.id} className="bg-ink-900 border border-ink-700 p-4">
            {editing === f.id ? (
              <EditRow row={f} onSave={save} onCancel={() => setEditing(null)} />
            ) : (
              <div>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap text-xs text-zinc-500 uppercase tracking-wider">
                      <span>page : {f.page_type}{f.page_id ? `/${f.page_id}` : ""}</span>
                      <span>•</span>
                      <span>ordre : {f.order || 0}</span>
                      {!f.active && <span className="text-red-400 font-bold">— masquée</span>}
                    </div>
                    <div className="font-bold mt-1">{f.question}</div>
                    <div className="text-sm text-zinc-300 mt-1">{f.answer}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => setEditing(f.id)} className="text-zinc-400 hover:text-brand text-xs font-bold uppercase">Modifier</button>
                    <button onClick={() => remove(f.id)} className="text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        {!rows.length && <div className="text-zinc-500 text-center py-8">Aucune FAQ</div>}
      </div>

      <style>{`.input-admin{ width:100%; height:40px; padding:0 12px; background:#3C3C40; border:1px solid #4A4A4D; color:#fff; font-size:14px; outline:none; } .input-admin:focus{ border-color:#FF6200;}`}</style>
    </div>
  );
}

function EditRow({ row, onSave, onCancel }) {
  const [r, setR] = useState(row);
  return (
    <div>
      <input className="input-admin" value={r.question} onChange={(e) => setR({ ...r, question: e.target.value })} />
      <textarea className="input-admin mt-2 h-24 py-2" value={r.answer} onChange={(e) => setR({ ...r, answer: e.target.value })} />
      <div className="grid sm:grid-cols-4 gap-2 mt-2">
        <input className="input-admin" value={r.page_type} onChange={(e) => setR({ ...r, page_type: e.target.value })} />
        <input className="input-admin" value={r.page_id || ""} onChange={(e) => setR({ ...r, page_id: e.target.value })} />
        <input className="input-admin" type="number" value={r.order || 0} onChange={(e) => setR({ ...r, order: e.target.value })} />
        <label className="text-sm text-zinc-300 inline-flex items-center gap-2"><input type="checkbox" checked={!!r.active} onChange={(e) => setR({ ...r, active: e.target.checked })} /> Actif</label>
      </div>
      <div className="mt-2 flex gap-2">
        <button onClick={() => onSave(r)} className="bg-brand hover:bg-brand-600 text-white px-4 py-2 text-sm font-bold uppercase inline-flex items-center gap-2"><Save size={14} /> Enregistrer</button>
        <button onClick={onCancel} className="bg-ink-800 text-white px-4 py-2 text-sm font-bold uppercase">Annuler</button>
      </div>
    </div>
  );
}
